import { Crown, Hand, Mic, MicOff, MonitorUp, MoreHorizontal, Search, UserPlus, Video, VideoOff, X } from 'lucide-react';
import { useRef, useState } from 'react';
import type { Participant } from '../../shared/protocol';
import { Avatar, Button, MenuItem, Modal, Popover } from '../components/ui';
import { useStore } from '../lib/store';
import type { MeetingSession } from '../meeting/MeetingSession';
import { ParticipantMenu } from './ParticipantMenu';

export function ParticipantsPanel({ session, onClose, onInvite }: { session: MeetingSession; onClose(): void; onInvite(): void }) {
  const s = useStore(session.store);
  const [query, setQuery] = useState('');
  const [muteAll, setMuteAll] = useState(false);
  const [footerMenu, setFooterMenu] = useState(false);
  const moreRef = useRef<HTMLButtonElement>(null);
  const selfId = s.selfId!;
  const amHost = s.participants[selfId]?.role === 'host';
  const q = query.trim().toLowerCase();

  const all = Object.values(s.participants).sort((a, b) => {
    const rank = (p: Participant) => (p.id === selfId ? 0 : p.role === 'host' ? 1 : p.handRaised ? 2 : 3);
    return rank(a) - rank(b) || a.joinedAt - b.joinedAt;
  });
  const list = q ? all.filter((p) => p.name.toLowerCase().includes(q)) : all;
  const waiting = amHost ? s.waiting.filter((w) => !q || w.name.toLowerCase().includes(q)) : [];

  return (
    <section className="panel" aria-label="Participantes">
      <div className="panel-header">
        <span>Participantes ({all.length})</span>
        <button type="button" className="icon-btn" aria-label="Cerrar participantes" onClick={onClose}>
          <X size={18} />
        </button>
      </div>
      <div className="panel-search">
        <label className="sr-only" htmlFor="plist-search">
          Buscar participantes
        </label>
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-3)' }} />
          <input id="plist-search" className="input" style={{ paddingLeft: 30 }} placeholder="Buscar" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
      </div>
      <div className="panel-body">
        {waiting.length > 0 && (
          <>
            <div className="plist-group">
              <span>En la sala de espera ({waiting.length})</span>
              <Button size="sm" variant="ghost" onClick={() => session.host({ action: 'admit-all' })}>
                Admitir a todos
              </Button>
            </div>
            {waiting.map((w) => (
              <div key={w.id} className="plist-row waiting-row">
                <Avatar name={w.name} seed={w.id} size={30} />
                <div className="plist-name">
                  <strong>{w.name}</strong>
                </div>
                <div className="plist-actions">
                  <Button size="sm" variant="primary" onClick={() => session.host({ action: 'admit', target: w.id })}>
                    Admitir
                  </Button>
                  <Button size="sm" onClick={() => session.host({ action: 'deny', target: w.id })} aria-label={`Rechazar a ${w.name}`}>
                    Rechazar
                  </Button>
                </div>
              </div>
            ))}
            <div className="plist-group">
              <span>En la reunión ({all.length})</span>
            </div>
          </>
        )}
        {list.map((p) => (
          <ParticipantRow key={p.id} session={session} participant={p} isSelf={p.id === selfId} amHost={amHost} />
        ))}
        {list.length === 0 && <p className="muted" style={{ textAlign: 'center' }}>Nadie coincide con «{query}»</p>}
      </div>
      <div className="panel-footer">
        <Button onClick={onInvite}>
          <UserPlus size={16} /> Invitar
        </Button>
        {amHost ? (
          <>
            <Button onClick={() => setMuteAll(true)}>Silenciar a todos</Button>
            <button ref={moreRef} type="button" className="icon-btn" aria-label="Más opciones de participantes" aria-haspopup="menu" onClick={() => setFooterMenu((v) => !v)}>
              <MoreHorizontal size={18} />
            </button>
          </>
        ) : (
          <Button onClick={() => session.setHandRaised(!s.participants[selfId]?.handRaised)}>
            <Hand size={16} /> {s.participants[selfId]?.handRaised ? 'Bajar la mano' : 'Levantar la mano'}
          </Button>
        )}
      </div>
      {footerMenu && amHost && (
        <Popover anchor={moreRef} onClose={() => setFooterMenu(false)} label="Opciones de participantes">
          <MenuItem checked={s.roomSettings.allowUnmute} onSelect={() => session.host({ action: 'settings', settings: { allowUnmute: !s.roomSettings.allowUnmute } })}>
            Permitir que reactiven su audio
          </MenuItem>
          <MenuItem checked={s.roomSettings.muteOnEntry} onSelect={() => session.host({ action: 'settings', settings: { muteOnEntry: !s.roomSettings.muteOnEntry } })}>
            Silenciar al entrar
          </MenuItem>
          <MenuItem checked={s.roomSettings.allowRename} onSelect={() => session.host({ action: 'settings', settings: { allowRename: !s.roomSettings.allowRename } })}>
            Permitir cambiar el nombre
          </MenuItem>
          <MenuItem checked={s.roomSettings.waitingRoom} onSelect={() => session.host({ action: 'settings', settings: { waitingRoom: !s.roomSettings.waitingRoom } })}>
            Sala de espera
          </MenuItem>
          <MenuItem checked={s.locked} onSelect={() => session.host({ action: 'lock', locked: !s.locked })}>
            Bloquear reunión
          </MenuItem>
        </Popover>
      )}
      {muteAll && <MuteAllDialog session={session} onClose={() => setMuteAll(false)} />}
    </section>
  );
}

function ParticipantRow({ session, participant: p, isSelf, amHost }: { session: MeetingSession; participant: Participant; isSelf: boolean; amHost: boolean }) {
  const [menu, setMenu] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const tags = [isSelf && 'Yo', p.role === 'host' && 'Anfitrión', !p.connected && 'reconectando…'].filter(Boolean).join(', ');
  return (
    <div className="plist-row">
      <Avatar name={p.name} seed={p.id} size={30} />
      <div className="plist-name">
        <strong>
          {p.name} {p.role === 'host' && <Crown size={12} style={{ color: 'var(--amber)', verticalAlign: -1 }} aria-hidden />}
        </strong>
        {tags && <small>({tags})</small>}
      </div>
      <div className={`plist-icons ${amHost && !isSelf ? 'hide-on-hover' : ''}`}>
        {p.handRaised && <Hand size={16} aria-label="Mano levantada" style={{ color: 'var(--amber)' }} />}
        {p.sharing && <MonitorUp size={16} aria-label="Compartiendo pantalla" style={{ color: 'var(--green)' }} />}
        {p.recording && <span className="rec-dot" role="img" aria-label="Grabando" title="Grabando la reunión" />}
        {p.audioMuted ? <MicOff size={16} className="off" aria-label="Micrófono silenciado" /> : <Mic size={16} aria-label="Micrófono activo" />}
        {p.videoOn ? <Video size={16} aria-label="Cámara encendida" /> : <VideoOff size={16} className="off" aria-label="Cámara apagada" />}
      </div>
      {(amHost || isSelf) && (
        <div className={`plist-actions ${menu ? 'open' : ''}`}>
          {amHost && !isSelf && (
            <Button size="sm" onClick={() => session.host(p.audioMuted ? { action: 'ask-unmute', target: p.id } : { action: 'mute', target: p.id })}>
              {p.audioMuted ? 'Pedir audio' : 'Silenciar'}
            </Button>
          )}
          <button ref={ref} type="button" className="icon-btn" aria-label={`Más acciones para ${p.name}`} aria-haspopup="menu" aria-expanded={menu} onClick={() => setMenu((v) => !v)}>
            <MoreHorizontal size={16} />
          </button>
          {menu && <ParticipantMenu session={session} participant={p} isSelf={isSelf} anchor={ref} onClose={() => setMenu(false)} placement="bottom" />}
        </div>
      )}
    </div>
  );
}

function MuteAllDialog({ session, onClose }: { session: MeetingSession; onClose(): void }) {
  const [allowUnmute, setAllowUnmute] = useState(session.store.get().roomSettings.allowUnmute);
  return (
    <Modal
      title="Silenciar a todos"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              session.host({ action: 'settings', settings: { allowUnmute } });
              session.host({ action: 'mute-all' });
              onClose();
            }}
          >
            Sí
          </Button>
        </>
      }
    >
      <p style={{ margin: 0 }}>Se silenciará a todos los participantes actuales y a los que entren después.</p>
      <label className="check">
        <input type="checkbox" checked={allowUnmute} onChange={(e) => setAllowUnmute(e.target.checked)} />
        Permitir que los participantes reactiven su audio ellos mismos
      </label>
    </Modal>
  );
}
